import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchTestTabFilteredList,
  fetchTestTabOngoing
} from '@api/fetchers/goal/test-tab';
import type {
  ApiTabId,
  TestTabFilterRequestBody
} from '@api/fetchers/goal/test-tab/types';
import { TEST_SERIES_TAB } from '@cont/SeeAll/TestSeriesListing/Common/TestSeriesTabSwitcher';
import {
  CATEGORY_TAB,
  LIST_PAGE_SIZE,
  getApiTabId,
  requiresAuthForTestTab
} from '@cont/SeeAll/TestSeriesListing/testTab/constants';
import {
  getFilteredListItemCount,
  mapListResponseToCards,
  mapOngoingToCards
} from '@cont/SeeAll/TestSeriesListing/testTab/mapToTestCard';
import type {
  CategoryTabId,
  TestListingCard,
  TestSeriesView
} from '@cont/SeeAll/TestSeriesListing/testTab/types';

interface FetchListPageParams {
  goalUid: string;
  apiTabId: ApiTabId;
  language: number;
  offset: number;
  filterBody: TestTabFilterRequestBody;
}

interface ListPageResult {
  cards: TestListingCard[];
  hasMore: boolean;
}

interface UseTestTabCardsParams {
  goalUid?: string;
  view: TestSeriesView;
  categoryTabId: CategoryTabId;
  filterBody: TestTabFilterRequestBody | null;
  language?: number;
  isLoggedIn?: boolean;
}

const fetchListPage = async ({
  goalUid,
  apiTabId,
  language,
  offset,
  filterBody
}: FetchListPageParams): Promise<ListPageResult> => {
  const response = await fetchTestTabFilteredList({
    goalUid,
    apiTabId,
    language,
    limit: LIST_PAGE_SIZE,
    offset,
    filterBody
  });

  const itemCount = getFilteredListItemCount(response);

  return {
    cards: mapListResponseToCards(response, apiTabId),
    hasMore: itemCount >= LIST_PAGE_SIZE
  };
};

const useTestTabCards = ({
  goalUid,
  view,
  categoryTabId,
  filterBody,
  language = 1,
  isLoggedIn = false
}: UseTestTabCardsParams) => {
  const apiTabId = getApiTabId(view, categoryTabId);
  const [offset, setOffset] = useState(0);
  const [extraCards, setExtraCards] = useState<TestListingCard[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [firstPageResult, setFirstPageResult] = useState<ListPageResult | null>(
    null
  );
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const isOngoingTab =
    view === TEST_SERIES_TAB.ALL && categoryTabId === CATEGORY_TAB.ONGOING;
  const needsAuth = requiresAuthForTestTab(view, categoryTabId, apiTabId);

  const shouldFetch = Boolean(
    goalUid && (!needsAuth || isLoggedIn) && apiTabId && filterBody
  );

  useEffect(() => {
    if (!shouldFetch || !goalUid) {
      setFirstPageResult(null);
      setError(null);
      setIsLoading(false);
      setIsValidating(false);
      setHasMore(false);
      setOffset(0);
      setExtraCards([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setOffset(0);
      setExtraCards([]);
      setIsLoading(true);
      setIsValidating(true);
      setError(null);

      try {
        let result: ListPageResult;

        if (isOngoingTab) {
          const response = await fetchTestTabOngoing({ goalUid, language });
          result = {
            cards: mapOngoingToCards(response),
            hasMore: false
          };
        } else if (!apiTabId || !filterBody) {
          result = { cards: [], hasMore: false };
        } else {
          result = await fetchListPage({
            goalUid,
            apiTabId,
            language,
            offset: 0,
            filterBody
          });
        }

        if (!cancelled) {
          setFirstPageResult(result);
          setHasMore(result.hasMore);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsValidating(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    apiTabId,
    filterBody,
    goalUid,
    isLoggedIn,
    isOngoingTab,
    language,
    needsAuth,
    shouldFetch
  ]);

  const firstPageCards = useMemo(
    () => firstPageResult?.cards ?? [],
    [firstPageResult?.cards]
  );

  const cards = useMemo(
    () => [...firstPageCards, ...extraCards],
    [extraCards, firstPageCards]
  );

  const loadMore = useCallback(async () => {
    if (
      !hasMore ||
      isOngoingTab ||
      !apiTabId ||
      !filterBody ||
      !goalUid ||
      isValidating
    ) {
      return;
    }

    setIsValidating(true);

    try {
      const nextOffset = offset + LIST_PAGE_SIZE;
      const nextPage = await fetchListPage({
        goalUid,
        apiTabId,
        language,
        offset: nextOffset,
        filterBody
      });

      setOffset(nextOffset);
      setExtraCards((prev) => [...prev, ...nextPage.cards]);
      setHasMore(nextPage.hasMore);
    } catch (fetchError) {
      setError(fetchError);
    } finally {
      setIsValidating(false);
    }
  }, [
    apiTabId,
    filterBody,
    goalUid,
    hasMore,
    isOngoingTab,
    isValidating,
    language,
    offset
  ]);

  return {
    cards,
    isInitialLoading: shouldFetch && isLoading && firstPageResult === null,
    isRefreshing: shouldFetch && isLoading && firstPageResult !== null,
    isError: Boolean(error) && firstPageResult === null,
    isValidating,
    hasMore,
    loadMore
  };
};

export default useTestTabCards;
